import { useCallback } from 'react';
import { Box, useTheme } from '@mui/material';
import { ConditionalLoader } from 'dashboard409a/pages/Operations/components';
import { StepLabelProps } from './StepLabel.props';
import { StyledStepLabel } from './StepLabel.styles';
import { getStepAccentColors } from '../getStepAccentColors';
import StepIcon from '../StepIcon/StepIcon';
import { StepStatus } from '../ValuationStatusTracker.types';


const CustomStepIcon = ({
  status,
  isLoading,
  accentColor,
}: {
  status: StepStatus;
  isLoading: boolean;
  accentColor?: string;
}) => <StepIcon status={status} isLoading={isLoading} accentColor={accentColor} />;

interface StepIconSlotProps {
  status: StepStatus;
  isLoading: boolean;
  accentColor?: string;
}

const StepIconSlot = ({ status, isLoading, accentColor }: StepIconSlotProps) => (
  <CustomStepIcon status={status} isLoading={isLoading} accentColor={accentColor} />
);

const StepLabel = ({ label, step, index, isLoading, disabled = false, rightContent, accentColor, ...rest }: StepLabelProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { activeColor, completedBackground, completedTextColor, completedDarkBorder } = getStepAccentColors(
    accentColor,
    isDark,
    theme.palette.primary.main
  );

  const getStepStyles = () => {
    const baseStyles = {
      padding: theme.spacing(0.75),
      borderRadius: theme.shape.borderRadius * 2,
      fontSize: theme.typography.body2.fontSize,
      display: 'inline-block',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    };

    switch (step) {
      case StepStatus.COMPLETED: {
        if (accentColor && completedBackground && completedTextColor) {
          return {
            ...baseStyles,
            backgroundColor: completedBackground,
            color: completedTextColor,
            ...(completedDarkBorder ? { border: completedDarkBorder } : {}),
          };
        }

        return {
          ...baseStyles,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
        };
      }

      case StepStatus.ACTIVE:
        return {
          ...baseStyles,
          border: `1px solid ${activeColor}`,
          color: activeColor,
        };

      case StepStatus.PENDING:
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: theme.palette.grey[400],
        };

      default:
        return baseStyles;
    }
  };

  const stepIconSlot = useCallback(
    () => (
      <StepIconSlot status={step} isLoading={isLoading || false} accentColor={accentColor} />
    ),
    [step, isLoading, accentColor]
  );

  return (
    <StyledStepLabel {...rest} slots={{ stepIcon: stepIconSlot }} onClick={undefined}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ConditionalLoader isLoading={isLoading} width={190}>
          <Box style={getStepStyles()} onClick={disabled ? undefined : () => rest.onClick?.()}>
            {label}
          </Box>
        </ConditionalLoader>
        {rightContent}
      </Box>
    </StyledStepLabel>
  );
};

export default StepLabel;
